import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useGetItemQuery, useUpdatePropertyMutation, useDeletePropertyMutation } from '../store/docApi';
import Steps from '../components/widgets/Steps';
import Markdown from '../components/widgets/Markdown';
import ItemMetadata from '../components/widgets/ItemMetadata';
import Measurements from './Measurements';
import Materials from './Materials';
import Needles from './Needles';
import Gauge from './Gauge';
import Abbreviations from './Abbreviations';
import OtherMaterials from './OtherMaterials';
import SkillLevel from './SkillLevel';
import YarnWeight from './YarnWeight';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintButton from './PrintButton';

export default function PatternDetails() {
  const { pk, id, subId } = useParams();
  const [originalPattern, setOriginalPattern] = useState(null);
  const [editedPattern, setEditedPattern] = useState(null);
  const { data: item } = useGetItemQuery({ label: 'workspace', pk, id });
  const [updateProperty] = useUpdatePropertyMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (item && item.patterns && item.patterns[subId]) {
      const patternData = item.patterns[subId];
      const defaultPattern = {
        name: '',
        author: '',
        description: '',
        skillLevel: '',
        yarnWeight: '',
        reference: '',
        stitchPattern: '',
        directions: '',
        measurements: {
          width: null,
          widthUnit: 'in',
          length: null,
          lengthUnit: 'in',
          ...(patternData.measurements || {})
        },
        materials: patternData.materials?.map(material => ({
          brand: material.brand || '',
          yarnName: material.yarnName || '',
          colorNumber: material.colorNumber || '',
          colorName: material.colorName || '',
          yardage: material.yardage != null ? Number(material.yardage) : null,
          meters: material.meters != null ? Number(material.meters) : null,
          weightOz: material.weightOz != null ? Number(material.weightOz) : null,
          weightGrams: material.weightGrams != null ? Number(material.weightGrams) : null,
          fiberContent: material.fiberContent || '',
          skeins: material.skeins != null ? Number(material.skeins) : null
        })) || [],
        needles: patternData.needles || [],
        gauge: {
          stitches: patternData.gauge?.stitches != null ? Number(patternData.gauge.stitches) : null,
          rows: patternData.gauge?.rows != null ? Number(patternData.gauge.rows) : null,
          needleSize: patternData.gauge?.needleSize || '',
          ...(patternData.gauge || {})
        },
        abbreviations: patternData.abbreviations || [],
        otherMaterials: Array.isArray(patternData.otherMaterials) ? patternData.otherMaterials : (patternData.otherMaterials ? [patternData.otherMaterials] : []),
        ...patternData
      };
      setOriginalPattern(defaultPattern);
      setEditedPattern({ ...defaultPattern });
    }
  }, [item, subId]);

  const handleSave = async () => {
    if (!editedPattern) return;

    try {
      await updateProperty({
        label: 'workspace',
        pk,
        id,
        name: `patterns/${subId}`,
        value: editedPattern
      });
      setOriginalPattern(editedPattern);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save pattern:', error);
    }
  };

  const handleCancel = () => {
    setEditedPattern({ ...originalPattern });
    setIsEditing(false);
  };

  function deletePattern() {
    setDeleteConfirmOpen(false);
    deleteProperty({ pk, id, name: `patterns/${subId}` });
    navigate(`/workspaces/${pk}/${id}`);
  }

  const handlePatternChange = (name, value) => {
    setEditedPattern(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    originalPattern && editedPattern && (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {isEditing ? (
              <input
                type="text"
                value={editedPattern.name || ''}
                onChange={(e) => handlePatternChange('name', e.target.value)}
                placeholder="Enter pattern name..."
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}
              />
            ) : (
              originalPattern.name
            )}
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              value={editedPattern.author || ''}
              onChange={(e) => handlePatternChange('author', e.target.value)}
              placeholder="Author name..."
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
          ) : originalPattern.author ? (
            <Typography variant="subtitle1">
              {originalPattern.author && `By ${originalPattern.author}`}
            </Typography>
          ) : null}
        </Box>
        {isEditing ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Reference</Typography>
            <TextField
              fullWidth
              value={editedPattern.reference || ''}
              onChange={(e) => handlePatternChange('reference', e.target.value)}
              placeholder="Reference..."
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
          </Box>) : originalPattern.reference && (
            <Box>
              <Typography variant="h6" gutterBottom>Reference for Stitch Pattern</Typography>
              <Typography variant="subtitle1">{originalPattern.reference}</Typography>
            </Box>
          )}
        {isEditing ? (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Description</Typography>
              <textarea
                value={editedPattern.description || ''}
                onChange={(e) => handlePatternChange('description', e.target.value)}
                placeholder="Enter pattern description..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '1rem'
                }}
              />
            </Box>

            <YarnWeight
              value={editedPattern.yarnWeight}
              onChange={handlePatternChange}
              editMode={true}
            />

            <SkillLevel
              value={editedPattern.skillLevel}
              onChange={handlePatternChange}
              editMode={true}
            />

            <Measurements
              value={editedPattern.measurements}
              onChange={handlePatternChange}
              editMode={true}
            />

            <Materials
              value={editedPattern.materials}
              onChange={handlePatternChange}
              editMode={true}
            />

            <OtherMaterials
              value={editedPattern.otherMaterials}
              onChange={handlePatternChange}
              editMode={true}
            />

            <Needles
              value={editedPattern.needles}
              onChange={handlePatternChange}
              editMode={true}
            />

            <Gauge
              value={editedPattern.gauge}
              onChange={handlePatternChange}
              editMode={true}
              patternName={editedPattern.name || 'pattern'}
            />

            <Abbreviations
              value={editedPattern.abbreviations}
              onChange={handlePatternChange}
              editMode={true}
            />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Stitch Patterns</Typography>
            <textarea
              value={editedPattern.stitchPattern || ''}
              onChange={(e) => handlePatternChange('stitchPattern', e.target.value)}
              placeholder="Enter stitch patterns..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '1rem'
              }}
            />
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Swatch Directions</Typography>
            <textarea
              value={editedPattern.directions || ''}
              onChange={(e) => handlePatternChange('directions', e.target.value)}
              placeholder="Enter swatch directions..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '1rem'
              }}
            />

          </Box>
        ) : (
          <Box>
            {originalPattern.description &&
              <div style={{ paddingBottom: 16 }}>
                <Markdown text={originalPattern.description} />
              </div>
            }
            <br />
            <YarnWeight value={originalPattern.yarnWeight} />
            <br />
            <SkillLevel value={originalPattern.skillLevel} />
            <br />
            <Measurements value={originalPattern.measurements} />

            <Materials value={originalPattern.materials} />
            <Needles value={originalPattern.needles} />
            <OtherMaterials value={originalPattern.otherMaterials} />

            <Gauge value={originalPattern.gauge} patternName={originalPattern.name} />
            <Abbreviations value={originalPattern.abbreviations} />

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Stitch Patterns</Typography>
              <Markdown text={originalPattern.stitchPattern} />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Swatch Directions</Typography>
              <Markdown text={originalPattern.directions} />
            </Box>
          </Box>
        )}


        {originalPattern.steps && <Steps steps={originalPattern.steps} showVerbal={true} expandAll={true} />}

        <ItemMetadata item={item} subitem={originalPattern} />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #eee', pt: 3 }}>
          {isEditing ? (
            <>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <PrintButton pattern={originalPattern} />
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit Pattern
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete Pattern
              </Button>
            </>
          )}
        </Box>

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth>
          <DialogTitle>Delete Pattern</DialogTitle>
          <DialogContent>
            <Typography>Delete this pattern from {item.name}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deletePattern}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  );
}
